import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export interface AdminDraftStudent {
  draft_id: string;
  family_id: string | null;
  company_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  status: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface AdminDraftStudentsResult {
  drafts: AdminDraftStudent[];
}

/**
 * Hook to fetch draft students for admin dashboard
 */
export function useAdminDraftStudents(companyId: string | null) {
  return useQuery<AdminDraftStudentsResult>({
    queryKey: ['admin', 'draft-students', companyId],
    enabled: !!companyId,
    staleTime: 30_000, // 30 seconds
    queryFn: async () => {
      if (!companyId) {
        throw new Error('Company ID is required');
      }

      // Query staging_import_members directly (not family_leads_view which filters by JWT company_id)
      // Admin should see all via RLS policies
      // Filter to get only family leads (not children) - same logic as family_leads_view
      const { data, error } = await supabase
        .from('staging_import_members')
        .select('id, family_id, company_id, first_name, last_name, email, phone_number, is_family_lead, invite_sent_at, invite_accepted_at, user_id, created_at, updated_at')
        .eq('company_id', companyId)
        .eq('is_archived', false)
        .order('created_at', { ascending: false });

      if (error) {
        // Throw a more descriptive error
        throw new Error(`Failed to load draft students: ${error.message || error.code || 'Unknown error'}`);
      }

      // Filter to only show family leads (same logic as family_leads_view)
      // Show: individual drafts (no family_id), drafts with invites, or family leads
      const filteredData = (data || []).filter((draft: {
        family_id: string | null;
        invite_sent_at: string | null;
        is_family_lead: boolean | null;
      }) => {
        // Individual (no family_id) - always show
        if (!draft.family_id || draft.family_id === '') {
          return true;
        }
        
        // Always show drafts with invites (so we can see invite status)
        if (draft.invite_sent_at) {
          return true;
        }
        
        // Show if explicitly marked as family lead
        if (draft.is_family_lead === true) {
          return true;
        }
        
        // Otherwise, exclude children
        return false;
      });

      // Compute status (same logic as family_leads_view)
      const drafts: AdminDraftStudent[] = filteredData.map((draft: {
        id: string;
        family_id: string | null;
        company_id: string;
        first_name: string | null;
        last_name: string | null;
        email: string | null;
        phone_number: string | null;
        user_id: string | null;
        invite_accepted_at: string | null;
        invite_sent_at: string | null;
        created_at: string;
        updated_at: string | null;
      }) => {
        let status = 'draft';
        if (draft.user_id) {
          status = 'registered';
        } else if (draft.invite_accepted_at) {
          status = 'accepted';
        } else if (draft.invite_sent_at) {
          status = 'invited';
        }

        return {
          draft_id: draft.id, // staging_import_members uses 'id' not 'draft_id'
          family_id: draft.family_id,
          company_id: draft.company_id,
          first_name: draft.first_name,
          last_name: draft.last_name,
          email: draft.email,
          phone: draft.phone_number, // Column is phone_number, not phone
          status,
          created_at: draft.created_at,
          updated_at: draft.updated_at,
        };
      });

      return { drafts };
    },
  });
}
